import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { bestQtyTier } from "../lib/shop";

const CartCtx = createContext(null);
const KEY = "anaiza_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); }
    catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, qty = 1) => {
    setItems((prev) => {
      const ex = prev.find((p) => p.product_id === product.id);
      if (ex) return prev.map((p) => p.product_id === product.id ? { ...p, quantity: p.quantity + qty } : p);
      return [...prev, {
        product_id: product.id,
        name: product.short_name,
        price: product.price,
        image: product.images?.[0] || "",
        quantity: qty,
        tiers: product.quantity_discount_tiers || [],
      }];
    });
    setOpen(true);
  };
  const update = (product_id, quantity) => {
    if (quantity <= 0) return remove(product_id);
    setItems((prev) => prev.map((p) => p.product_id === product_id ? { ...p, quantity } : p));
  };
  const remove = (product_id) => setItems((prev) => prev.filter((p) => p.product_id !== product_id));
  const clear = () => setItems([]);

  // gross subtotal (before any discount)
  const grossSubtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);

  // line discount total (from quantity tiers)
  const lineDiscountTotal = useMemo(() => items.reduce((s, i) => {
    const tier = bestQtyTier(i.tiers, i.quantity);
    return s + (i.price * i.quantity * tier.percent) / 100;
  }, 0), [items]);

  // net subtotal (after line tier discounts) – this is what feeds into 500 RON order discount
  const subtotal = useMemo(() => Math.max(0, grossSubtotal - lineDiscountTotal), [grossSubtotal, lineDiscountTotal]);

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return (
    <CartCtx.Provider value={{ items, add, update, remove, clear, subtotal, grossSubtotal, lineDiscountTotal, count, open, setOpen }}>
      {children}
    </CartCtx.Provider>
  );
}
export const useCart = () => useContext(CartCtx);
